/* eslint-disable implicit-arrow-linebreak */
import React from 'react';
import PropTypes from 'prop-types';
import { useNamespace, useStripes } from '@folio/stripes/core';
import { useQueries } from 'react-query';
import { flatMap, keyBy, merge, omit } from 'lodash';
import useTenantKy from './useTenantKy';

const manifest = {
  identifierTypes: {
    type: 'okapi',
    records: 'identifierTypes',
    path: 'identifier-types?limit=1000&query=cql.allRecords=1 sortby name',
  },
  contributorTypes: {
    type: 'okapi',
    records: 'contributorTypes',
    path: 'contributor-types?limit=400&query=cql.allRecords=1 sortby name',
  },
  contributorNameTypes: {
    type: 'okapi',
    records: 'contributorNameTypes',
    path: 'contributor-name-types?limit=1000&query=cql.allRecords=1 sortby ordering',
  },
  instanceFormats: {
    type: 'okapi',
    records: 'instanceFormats',
    path: 'instance-formats?limit=1000&query=cql.allRecords=1 sortby name',
  },
  instanceTypes: {
    type: 'okapi',
    records: 'instanceTypes',
    path: 'instance-types?limit=1000&query=cql.allRecords=1 sortby name',
  },
  classificationTypes: {
    type: 'okapi',
    records: 'classificationTypes',
    path: 'classification-types?limit=1000&query=cql.allRecords=1 sortby name',
  },
  alternativeTitleTypes: {
    type: 'okapi',
    records: 'alternativeTitleTypes',
    path: 'alternative-title-types?limit=1000&query=cql.allRecords=1 sortby name',
  },
  locations: {
    type: 'okapi',
    records: 'locations',
    path: 'locations?limit=1000',
  },
  instanceRelationshipTypes: {
    type: 'okapi',
    records: 'instanceRelationshipTypes',
    path: 'instance-relationship-types?limit=1000&query=cql.allRecords=1 sortby name',
  },
  instanceStatuses: {
    type: 'okapi',
    records: 'instanceStatuses',
    path: 'instance-statuses?limit=1000&query=cql.allRecords=1 sortby name',
  },
  modesOfIssuance: {
    type: 'okapi',
    records: 'issuanceModes',
    path: 'modes-of-issuance?limit=1000&query=cql.allRecords=1 sortby name',
  },
  instanceNoteTypes: {
    type: 'okapi',
    path: 'instance-note-types',
    params: {
      query: 'cql.allRecords=1 sortby name',
      limit: '1000',
    },
    records: 'instanceNoteTypes',
  },
  electronicAccessRelationships: {
    type: 'okapi',
    records: 'electronicAccessRelationships',
    path: 'electronic-access-relationships?limit=1000&query=cql.allRecords=1 sortby name',
  },
  statisticalCodeTypes: {
    type: 'okapi',
    records: 'statisticalCodeTypes',
    path: 'statistical-code-types?limit=1000&query=cql.allRecords=1 sortby name',
  },
  statisticalCodes: {
    type: 'okapi',
    records: 'statisticalCodes',
    path: 'statistical-codes?limit=2000&query=cql.allRecords=1 sortby name',
  },
  illPolicies: {
    type: 'okapi',
    path: 'ill-policies?limit=1000&query=cql.allRecords=1 sortby name',
    records: 'illPolicies',
  },
  holdingsTypes: {
    type: 'okapi',
    path: 'holdings-types?limit=1000&query=cql.allRecords=1 sortby name',
    records: 'holdingsTypes',
  },
  callNumberTypes: {
    type: 'okapi',
    path: 'call-number-types?limit=1000&query=cql.allRecords=1 sortby name',
    records: 'callNumberTypes',
  },
  holdingsNoteTypes: {
    type: 'okapi',
    path: 'holdings-note-types?limit=1000&query=cql.allRecords=1 sortby name',
    records: 'holdingsNoteTypes',
  },
  itemNoteTypes: {
    type: 'okapi',
    path: 'item-note-types',
    params: {
      query: 'cql.allRecords=1 sortby name',
      limit: '1000',
    },
    records: 'itemNoteTypes',
  },
  itemDamagedStatuses: {
    type: 'okapi',
    path: 'item-damaged-statuses?limit=1000&query=cql.allRecords=1 sortby name',
    records: 'itemDamageStatuses',
  },
  natureOfContentTerms: {
    type: 'okapi',
    path: 'nature-of-content-terms?limit=1000&query=cql.allRecords=1 sortby name',
    records: 'natureOfContentTerms',
  },
  materialTypes: {
    type: 'okapi',
    path: 'material-types?limit=10000',
    params: {
      query: 'cql.allRecords=1 sortby name',
      limit: '1000',
    },
    records: 'mtypes',
  },
  loantypes: {
    type: 'okapi',
    path: 'loan-types?limit=10000',
    params: {
      query: 'cql.allRecords=1 sortby name',
      limit: '1000',
    },
    records: 'loantypes',
  },
  tags: {
    path: 'tags?limit=10000', // the same as Tags component in stripes-smart-components
    records: 'tags',
    throwErrors: false,
    type: 'okapi',
  },
  holdingsSources: {
    type: 'okapi',
    path: 'holdings-sources',
    params: {
      query: 'cql.allRecords=1 sortby name',
      limit: '1000',
    },
    records: 'holdingsRecordsSources',
  },
  tenantLocales: {
    type: 'okapi',
    records: 'locales',
    path: 'tenant-locales?limit=1000',
  },
  inventoryTemplates: {
    type: 'okapi',
    records: 'templates',
    path: 'inventory-templates?limit=1000',
  },
  inventoryDefaultFields: {
    type: 'okapi',
    records: 'defaultFields',
    path: 'inventory-defaultFields?limit=1000',
  },
  inventoryCustomFields: {
    type: 'okapi',
    records: 'customFields',
    path: 'inventory-customFields?limit=1000',
  },
  inventoryTemplatesConfigs: {
    type: 'okapi',
    records: 'templatesConfigs',
    path: 'inventory-templates-configs?limit=1000',
  },
  authoritySourceFiles: {
    type: 'okapi',
    records: 'authoritySourceFiles',
    path: 'authority-source-files?limit=1000&query=cql.allRecords=1 sortby name',
  },
};

function useControlledVocab(props) {
  const {
    okapi: { tenant },
  } = useStripes();
  const ky = useTenantKy({ tenantId: tenant });
  const [namespace] = useNamespace({ key: 'dataQueries' });

  const dataQueries = useQueries(
    Object.keys(manifest).map((key) => {
      return {
        enabled: true,
        queryKey: [namespace, key, tenant],
        queryFn: () => ky.get(manifest[key].path).json(),
      };
    }),
  );

  const data = dataQueries.map((result) => omit(result.data, ['totalRecords']));
  const { mtypes, instanceRelationshipTypes, issuanceModes, authoritySourceFiles, ...rest } = merge(
    {},
    ...data,
  );
  const instanceRelationshipTypesById = keyBy(instanceRelationshipTypes, 'id');

  return {
    data: {
      ...rest,
      materialTypes: mtypes,
      modesOfIssuance: issuanceModes,
      instanceRelationshipTypesById,
      authoritySourceFiles: authoritySourceFiles || [],
    },
    pending: dataQueries.some(({ isFetching }) => isFetching),
  };
}

useControlledVocab.propTypes = {};

export default useControlledVocab;
